import { useState } from 'react';
import type {
  WaterData,
  DHWSystemType,
  HeatSource,
  CylinderVolumeBand,
  CylinderTypeProfile,
  CirculationMode,
  PipeRunBand,
  HardnessBand,
  ScaleProfile,
} from '../types';

const DHW_TYPE_OPTIONS: { value: DHWSystemType; label: string }[] = [
  { value: 'combi-on-demand', label: 'Combi on-demand' },
  { value: 'vented-cylinder', label: 'Vented cylinder' },
  { value: 'unvented-cylinder', label: 'Unvented cylinder' },
  { value: 'thermal-store', label: 'Thermal store' },
  { value: 'unknown', label: 'Unknown' },
];

const DHW_SOURCE_OPTIONS: { value: HeatSource; label: string }[] = [
  { value: 'gas', label: 'Gas' },
  { value: 'oil', label: 'Oil' },
  { value: 'electric', label: 'Immersion' },
  { value: 'heat-pump', label: 'Heat pump' },
  { value: 'unknown', label: 'Unknown' },
];

const VOLUME_OPTIONS: { value: CylinderVolumeBand; label: string }[] = [
  { value: 'small', label: 'Small (<90L)' },
  { value: 'normal-vented', label: 'Normal vented (90–105L)' },
  { value: 'large', label: 'Large (120–180L)' },
  { value: 'xlarge', label: 'XL (200L+)' },
  { value: 'unknown', label: 'Unknown' },
];

const CYLINDER_TYPE_OPTIONS: { value: CylinderTypeProfile; label: string; standard: string; colour: string; standingLossRange: string; description?: string; recommended?: boolean; isModern?: boolean; isLegacy?: boolean; isSmart?: boolean }[] = [
  { value: 'high-performance', label: 'High Performance', standard: 'Part L 2021/2025 (New Build)', colour: 'Blue / Silver', standingLossRange: '0.7 – 1.1 kWh', description: 'Latest high-efficiency cylinders with excellent insulation. Typically blue or silver foam. Best thermal performance for new builds and premium replacements.', recommended: true, isModern: true },
  { value: 'standard-modern', label: 'Standard Modern', standard: 'Part L 2010 (Replacement)', colour: 'Green / White', standingLossRange: '1.2 – 1.6 kWh', description: 'Modern replacement cylinders meeting Part L 2010. Green or white foam insulation. Good performance for most replacements.', recommended: true, isModern: true },
  { value: 'mixergy-unvented', label: 'Mixergy Unvented (Smart)', standard: 'Smart Cylinder (Mains Pressure)', colour: 'Smart White/Blue', standingLossRange: '0.4 – 0.7 kWh', description: 'Premium smart unvented cylinder with app control. Excellent stratification reduces standing losses by 40% compared to standard unvented (0.6 vs 1.2 kWh/day). Perfect for solar PV, off-peak tariffs, and multi-outlet mains pressure hot water. Works with system or regular boilers (sealed). Requires G3 maintenance.', recommended: true, isModern: true, isSmart: true },
  { value: 'mixergy-vented', label: 'Mixergy Vented (Smart)', standard: 'Smart Cylinder (Open-Vented)', colour: 'Smart White/Blue', standingLossRange: '0.5 – 0.8 kWh', description: 'Smart stratified cylinder with app control. Reduces standing losses by 40% compared to standard vented (0.8 vs 1.8 kWh/day) through intelligent volumetric heating. Works with regular or system boilers. Excellent for solar PV integration. Requires loft tank but delivers smart control.', recommended: true, isModern: true, isSmart: true },
  { value: 'direct-electric', label: 'Direct Electric', standard: 'ErP Grade A (Best in Class)', colour: 'Encased White', standingLossRange: '0.5 – 0.9 kWh', description: 'Electric immersion heater cylinders. White casing with excellent insulation. Best for off-peak tariffs and when gas is unavailable.', isModern: true },
  { value: 'legacy-vented', label: 'Legacy Vented', standard: 'Pre-2010 Part L', colour: 'Green Foam', standingLossRange: '1.8 – 2.5 kWh', description: 'Older vented cylinders with green foam insulation. Installed pre-2010. Adequate performance but higher standing losses compared to modern cylinders.', isLegacy: true },
  { value: 'vintage-uninsulated', label: 'Vintage Uninsulated', standard: 'Pre-Part L (Loose Jacket)', colour: 'Red / Green Jacket', standingLossRange: '3.0 – 5.0+ kWh', description: 'Old cylinders with removable insulation jackets. Very poor thermal performance with significant heat losses. Replacement strongly recommended.', isLegacy: true },
  { value: 'unknown', label: 'Unknown', standard: 'Unknown', colour: 'Unknown', standingLossRange: '—', description: 'Cylinder type not yet identified. Standing losses cannot be estimated.' },
];

const CIRC_OPTIONS: { value: CirculationMode; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'keep-hot', label: 'Keep-hot' },
  { value: 'bronze-return-24-7', label: 'Bronze secondary return (24/7)' },
  { value: 'bronze-return-timed', label: 'Bronze secondary return (timed)' },
  { value: 'unknown', label: 'Unknown' },
];

const PIPE_RUN_OPTIONS: { value: PipeRunBand; label: string }[] = [
  { value: 'short', label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long', label: 'Long' },
  { value: 'unknown', label: 'Unknown' },
];

const HARDNESS_OPTIONS: { value: HardnessBand; label: string }[] = [
  { value: 'soft', label: 'Soft' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'hard', label: 'Hard' },
  { value: 'very-hard', label: 'Very hard' },
  { value: 'unknown', label: 'Unknown' },
];

const SCALE_OPTIONS: { value: ScaleProfile; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'heavy', label: 'Heavy' },
  { value: 'silica-leaning', label: 'Silica-leaning' },
  { value: 'unknown', label: 'Unknown' },
];


function parseStandingLossMidpoint(rangeLabel: string): number | undefined {
  if (!rangeLabel || rangeLabel === '—') return undefined;
  const values = Array.from(rangeLabel.matchAll(/\d+(?:\.\d+)?/g)).map((m) => Number.parseFloat(m[0])).filter((v) => Number.isFinite(v));
  if (values.length === 0) return undefined;
  if (values.length === 1) return values[0];
  return (values[0] + values[1]) / 2;
}

interface Props {
  data: WaterData;
  onSave: (data: WaterData) => void;
  onClose: () => void;
}

export function WaterScreen({ data, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<WaterData>({ ...data });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showCylinderInfo, setShowCylinderInfo] = useState<CylinderTypeProfile | null>(null);

  const set = <K extends keyof WaterData>(key: K, value: WaterData[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const hasBronzeReturn = draft.circulationMode === 'bronze-return-24-7' || draft.circulationMode === 'bronze-return-timed';
  const showCylinder = draft.dhwSystemType !== 'combi-on-demand';

  const applyCylinderType = (value: CylinderTypeProfile) => {
    const selected = CYLINDER_TYPE_OPTIONS.find(opt => opt.value === value);
    const standingLossLabel = selected?.standingLossRange ? `${selected.standingLossRange}/day` : draft.standingLoss;
    const midpoint = parseStandingLossMidpoint(selected?.standingLossRange ?? '');

    setDraft(prev => ({
      ...prev,
      cylinderTypeProfile: value,
      standingLoss: standingLossLabel,
      currentStandingLossKwhPerDay: midpoint,
      proposedStandingLossKwhPerDay: midpoint,
      standingLossSource: midpoint !== undefined ? 'estimated_from_band' : prev.standingLossSource,
    }));
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    setHasUnsavedChanges(false);
    onSave(draft);
  };

  // Compute derived values for cleaner JSX
  const selectedCylinder = draft.cylinderTypeProfile && draft.cylinderTypeProfile !== 'unknown'
    ? CYLINDER_TYPE_OPTIONS.find(o => o.value === draft.cylinderTypeProfile)
    : null;

  const cylinderInfoOption = showCylinderInfo
    ? CYLINDER_TYPE_OPTIONS.find(o => o.value === showCylinderInfo)
    : null;

  return (
    <>
      <div className="hub-screen-overlay">
      <div className="hub-screen" onClick={e => e.stopPropagation()}>
        <div className="hub-screen__header">
          <h2 className="hub-screen__title">Water (DHW)</h2>
          <button className="hub-screen__close" onClick={handleClose}>&times;</button>
        </div>

        <div className="hub-screen__body">
          {/* DHW system type + heat source */}
          <div className="hub-field">
            <label className="hub-field__label">DHW system type ("Is combi best?" — sometimes)</label>
            <span className="hub-field__hint">
              Match hot water architecture to household demand patterns and outlet concurrency, not trends.
            </span>
            <div className="hub-field__chip-group">
              {DHW_TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.dhwSystemType === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('dhwSystemType', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">DHW heat source</label>
            <div className="hub-field__chip-group">
              {DHW_SOURCE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.dhwHeatSource === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('dhwHeatSource', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Occupants & usage */}
          <div className="hub-field">
            <label className="hub-field__label">Occupants</label>
            <select value={draft.occupants} onChange={e => set('occupants', e.target.value)}>
              <option value="">—</option>
              {['1', '2', '3', '4', '5', '6+'].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          {/* Cylinder details */}
          {showCylinder && (
            <>
              <div className="hub-field">
                <label className="hub-field__label">Cylinder volume band</label>
                <div className="hub-field__chip-group">
                  {VOLUME_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`hub-chip${draft.cylinderVolumeBand === opt.value ? ' hub-chip--selected' : ''}`}
                      onClick={() => set('cylinderVolumeBand', opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hub-field">
                <label className="hub-field__label">Standing loss</label>
                <span className="hub-field__hint">
                  Standing loss is an optimisation layer. Pick the architecture first, then refine cylinder efficiency for your scenario.
                </span>
                
                {/* Smart Cylinders Section - Prominent */}
                <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', border: '2px solid #22c55e', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#15803d', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>⚡</span>
                    <span>Smart hot water cylinders (efficiency modifier)</span>
                  </div>
                  <div className="hub-field__chip-group">
                    {CYLINDER_TYPE_OPTIONS.filter(opt => opt.isSmart).map(opt => (
                      <div key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button
                          className={`hub-chip${draft.cylinderTypeProfile === opt.value ? ' hub-chip--selected' : ''}`}
                          onClick={() => applyCylinderType(opt.value)}
                          style={{
                            background: draft.cylinderTypeProfile === opt.value ? '#22c55e' : '#dcfce7',
                            color: draft.cylinderTypeProfile === opt.value ? 'white' : '#15803d',
                            border: '1px solid #22c55e',
                          }}
                        >
                          {opt.label}
                        </button>
                        <button
                          onClick={() => setShowCylinderInfo(opt.value)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            padding: '2px 4px',
                            opacity: 0.8,
                          }}
                          title="Tell me more"
                        >
                          ℹ️
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.85rem', marginTop: 6, color: '#166534' }}>
                    Smart stratification heats only what's needed, reducing losses by 40% vs. standard cylinders
                  </div>
                </div>

                {/* Modern Cylinders Section - Recommended */}
                <div style={{ marginTop: 12, padding: 12, background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1e40af' }}>
                    Modern Cylinders (Recommended for New Installs)
                  </div>
                  <div className="hub-field__chip-group">
                    {CYLINDER_TYPE_OPTIONS.filter(opt => opt.isModern && !opt.isSmart && opt.value !== 'direct-electric').map(opt => (
                      <div key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button
                          className={`hub-chip${draft.cylinderTypeProfile === opt.value ? ' hub-chip--selected' : ''}`}
                          onClick={() => applyCylinderType(opt.value)}
                        >
                          {opt.label}
                        </button>
                        <button
                          onClick={() => setShowCylinderInfo(opt.value)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            padding: '2px 4px',
                            opacity: 0.6,
                          }}
                          title="Tell me more"
                        >
                          ℹ️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legacy Cylinders Section - Warning */}
                <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>⚠️</span>
                    <span>Legacy Cylinders (Higher Standing Loss)</span>
                  </div>
                  <div className="hub-field__chip-group">
                    {CYLINDER_TYPE_OPTIONS.filter(opt => opt.isLegacy).map(opt => (
                      <div key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button
                          className={`hub-chip${draft.cylinderTypeProfile === opt.value ? ' hub-chip--selected' : ''}`}
                          onClick={() => applyCylinderType(opt.value)}
                          style={{
                            background: draft.cylinderTypeProfile === opt.value ? '#dc2626' : 'white',
                            color: draft.cylinderTypeProfile === opt.value ? 'white' : '#991b1b',
                            border: '1px solid #dc2626',
                          }}
                        >
                          {opt.label}
                        </button>
                        <button
                          onClick={() => setShowCylinderInfo(opt.value)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            padding: '2px 4px',
                            opacity: 0.6,
                          }}
                          title="Tell me more"
                        >
                          ℹ️
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.85rem', marginTop: 6, color: '#7f1d1d' }}>
                    These older cylinders have significantly higher heat losses. Consider upgrading to modern insulation standards.
                  </div>
                </div>

                {/* Other Cylinders */}
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 500, marginBottom: 6, fontSize: '0.9rem' }}>Other Options</div>
                  <div className="hub-field__chip-group">
                    {CYLINDER_TYPE_OPTIONS.filter(opt => !opt.isModern && !opt.isLegacy && opt.value !== 'unknown').map(opt => (
                      <div key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button
                          className={`hub-chip${draft.cylinderTypeProfile === opt.value ? ' hub-chip--selected' : ''}`}
                          onClick={() => applyCylinderType(opt.value)}
                        >
                          {opt.label}
                        </button>
                        {opt.description && (
                          <button
                            onClick={() => setShowCylinderInfo(opt.value)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              padding: '2px 4px',
                              opacity: 0.6,
                            }}
                            title="Tell me more"
                          >
                            ℹ️
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className={`hub-chip${draft.cylinderTypeProfile === 'unknown' ? ' hub-chip--selected' : ''}`}
                      onClick={() => applyCylinderType('unknown')}
                    >
                      Unknown
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ textAlign: 'left', padding: 8 }}>Cylinder Type</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Compliance / Standard</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Insulation Colour</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Standing Loss (kWh/24h)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Smart Cylinders */}
                      {CYLINDER_TYPE_OPTIONS.filter(opt => opt.isSmart).map(opt => (
                        <tr key={opt.value} style={{ borderTop: '1px solid #e5e7eb', background: '#f0fdf4' }}>
                          <td style={{ padding: 8, fontWeight: 600, color: '#15803d' }}>⚡ {opt.label}</td>
                          <td style={{ padding: 8 }}>{opt.standard}</td>
                          <td style={{ padding: 8 }}>{opt.colour}</td>
                          <td style={{ padding: 8, fontWeight: 600, color: '#15803d' }}>{opt.standingLossRange}</td>
                        </tr>
                      ))}
                      {/* Modern Cylinders */}
                      {CYLINDER_TYPE_OPTIONS.filter(opt => opt.isModern && !opt.isSmart).map(opt => (
                        <tr key={opt.value} style={{ borderTop: '1px solid #e5e7eb', background: '#eff6ff' }}>
                          <td style={{ padding: 8 }}>{opt.label}</td>
                          <td style={{ padding: 8 }}>{opt.standard}</td>
                          <td style={{ padding: 8 }}>{opt.colour}</td>
                          <td style={{ padding: 8 }}>{opt.standingLossRange}</td>
                        </tr>
                      ))}
                      {/* Legacy Cylinders */}
                      {CYLINDER_TYPE_OPTIONS.filter(opt => opt.isLegacy).map(opt => (
                        <tr key={opt.value} style={{ borderTop: '1px solid #e5e7eb', background: '#fef2f2' }}>
                          <td style={{ padding: 8, color: '#991b1b' }}>⚠️ {opt.label}</td>
                          <td style={{ padding: 8 }}>{opt.standard}</td>
                          <td style={{ padding: 8 }}>{opt.colour}</td>
                          <td style={{ padding: 8, color: '#991b1b' }}>{opt.standingLossRange}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Display selected cylinder's standing loss prominently */}
                {selectedCylinder && (
                  <div style={{ 
                    marginTop: 10, 
                    padding: 12, 
                    background: selectedCylinder.isSmart ? '#f0fdf4' : selectedCylinder.isLegacy ? '#fef2f2' : '#f0f9ff', 
                    border: `2px solid ${selectedCylinder.isSmart ? '#22c55e' : selectedCylinder.isLegacy ? '#dc2626' : '#0284c7'}`, 
                    borderRadius: 8 
                  }}>
                    <div style={{ 
                      fontWeight: 600, 
                      marginBottom: 4, 
                      color: selectedCylinder.isSmart ? '#15803d' : selectedCylinder.isLegacy ? '#991b1b' : '#0369a1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      {selectedCylinder.isSmart && <span>⚡</span>}
                      {selectedCylinder.isLegacy && <span>⚠️</span>}
                      <span>Selected: {selectedCylinder.label}</span>
                    </div>
                    <div style={{ 
                      fontSize: '0.9rem', 
                      color: selectedCylinder.isSmart ? '#166534' : selectedCylinder.isLegacy ? '#7f1d1d' : '#0c4a6e' 
                    }}>
                      Standing loss: <strong>{selectedCylinder.standingLossRange}</strong>
                    </div>
                    {selectedCylinder.isSmart && (
                      <div style={{ fontSize: '0.85rem', marginTop: 6, color: '#166534', fontStyle: 'italic' }}>
                        Smart stratification reduces standing loss by 40% compared to standard cylinders
                      </div>
                    )}
                    {selectedCylinder.isLegacy && (
                      <div style={{ fontSize: '0.85rem', marginTop: 6, color: '#7f1d1d', fontStyle: 'italic' }}>
                        ⚠️ Higher standing losses impact overall system efficiency. Consider upgrading to modern standards.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* DHW Distribution */}
          <div className="hub-field">
            <label className="hub-field__label">Pipe run band</label>
            <div className="hub-field__chip-group">
              {PIPE_RUN_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.pipeRunBand === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('pipeRunBand', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Pipe insulation</label>
            <div className="hub-field__chip-group">
              {['yes', 'no'].map(value => (
                <button
                  key={value}
                  className={`hub-chip${draft.pipeInsulation === value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('pipeInsulation', value)}
                >
                  {value === 'yes' ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Circulation mode</label>
            <div className="hub-field__chip-group">
              {CIRC_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.circulationMode === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('circulationMode', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {hasBronzeReturn && (
            <div className="hub-note hub-note--warning">
              Bronze secondary return eliminates DHW lag but adds continuous losses.
              Not compatible with combi boilers or smart stratified cylinders (e.g., Mixergy).
            </div>
          )}

          {/* Water quality */}
          <div className="hub-field">
            <label className="hub-field__label">Water hardness</label>
            <div className="hub-field__chip-group">
              {HARDNESS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.hardnessBand === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('hardnessBand', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Scale profile</label>
            <div className="hub-field__chip-group">
              {SCALE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.scaleProfile === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('scaleProfile', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="hub-screen__footer">
          <button className="hub-screen__button hub-screen__button--secondary" onClick={handleClose}>Cancel</button>
          <button className="hub-screen__button hub-screen__button--primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>

    {/* Discard changes dialog */}
    {showDiscardDialog && (
      <div className="hub-screen-overlay" style={{ zIndex: 1001 }}>
        <div className="hub-screen hub-screen--dialog" onClick={e => e.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">Discard changes?</h2>
          </div>
          <div className="hub-screen__body">
            <p>You have unsaved changes. Are you sure you want to discard them?</p>
          </div>
          <div className="hub-screen__footer">
            <button 
              className="hub-screen__button hub-screen__button--secondary" 
              onClick={() => setShowDiscardDialog(false)}
            >
              Keep editing
            </button>
            <button 
              className="hub-screen__button hub-screen__button--danger" 
              onClick={() => {
                setShowDiscardDialog(false);
                setHasUnsavedChanges(false);
                onClose();
              }}
            >
              Discard changes
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Cylinder info dialog */}
    {cylinderInfoOption && (
      <div className="hub-screen-overlay" style={{ zIndex: 1001 }}>
        <div className="hub-screen hub-screen--dialog" onClick={e => e.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">
              {cylinderInfoOption.label}
            </h2>
            <button className="hub-screen__close" onClick={() => setShowCylinderInfo(null)}>&times;</button>
          </div>
          <div className="hub-screen__body">
            <div>
              <div style={{ marginBottom: 16 }}>
                <strong>Compliance / Standard:</strong> {cylinderInfoOption.standard}
              </div>
              <div style={{ marginBottom: 16 }}>
                <strong>Typical Insulation Colour:</strong> {cylinderInfoOption.colour}
              </div>
              <div style={{ marginBottom: 16 }}>
                <strong>Standing Loss (per 24 hours):</strong> {cylinderInfoOption.standingLossRange}
              </div>
              <div style={{ 
                padding: 12, 
                background: '#f8fafc', 
                borderRadius: 8, 
                marginTop: 16,
                lineHeight: 1.6 
              }}>
                <strong>About this cylinder type:</strong>
                <p style={{ marginTop: 8, marginBottom: 0 }}>{cylinderInfoOption.description}</p>
              </div>
            </div>
            </div>
          <div className="hub-screen__footer">
            <button 
              className="hub-screen__button hub-screen__button--primary" 
              onClick={() => setShowCylinderInfo(null)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
